import { Minus, Plus } from "lucide-react";

// Cap at just under an octave; a full octave is musically identical. Must stay
// in sync with the clamp in useTranspose so the disabled state matches the
// values the hook will actually store.
const MAX_SEMITONES = 11;
const MIN_SEMITONES = -11;

type Props = {
  value: number;
  onChange: (value: number) => void;
};

/**
 * Stepper for transposing chords up or down by semitones.
 *
 * The readout shows the signed offset (e.g. "+2", "0", "-3"). Tapping the
 * readout resets to the original key. Mirrors CheckboxToggle's typography
 * so the two controls sit together comfortably.
 */
export function TransposeControl({ value, onChange }: Props) {
  const label = value > 0 ? `+${value}` : String(value);

  return (
    <div className="flex items-center gap-1 font-mono text-xs font-medium uppercase tracking-widest text-text-secondary">
      <span className="mr-0.5">Key</span>
      <button
        type="button"
        onClick={() => onChange(value - 1)}
        disabled={value <= MIN_SEMITONES}
        aria-label="Transpose down a semitone"
        className="flex h-5 w-5 items-center justify-center rounded border border-border-accent bg-bg-secondary transition-colors hover:bg-bg-tertiary disabled:cursor-not-allowed disabled:opacity-40"
      >
        <Minus className="h-3 w-3" aria-hidden="true" />
      </button>
      <button
        type="button"
        onClick={() => onChange(0)}
        disabled={value === 0}
        aria-label="Reset to original key"
        title="Reset to original key"
        className="min-w-[2ch] text-center tabular-nums transition-colors enabled:hover:text-amber disabled:cursor-default"
      >
        {label}
      </button>
      <button
        type="button"
        onClick={() => onChange(value + 1)}
        disabled={value >= MAX_SEMITONES}
        aria-label="Transpose up a semitone"
        className="flex h-5 w-5 items-center justify-center rounded border border-border-accent bg-bg-secondary transition-colors hover:bg-bg-tertiary disabled:cursor-not-allowed disabled:opacity-40"
      >
        <Plus className="h-3 w-3" aria-hidden="true" />
      </button>
    </div>
  );
}
